import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation, Trans} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_CACHE_KEYS} from '../../utils/constants';
import {BookingSummary} from '@guestapp/sdk';
import {PATHS} from 'Routes';
import {StatusModalStates} from 'components/StatusModal';
import {usePath, useTimeoutRef} from 'hooks';
import {
  StyledModalGuestRegistered,
  ModalSubtitle,
  ModalHeader,
  Row,
  ModalBody,
  ModalTitleText,
  ErrorMessage,
  CloseButton,
} from './styled';

const getTitle = (state?: StatusModalStates, defaultTitle?: string) => {
  if (state === StatusModalStates.LOADING) {
    return 'sending_your_data';
  } else if (defaultTitle) {
    return defaultTitle;
  } else if (state === StatusModalStates.ERROR) {
    return 'error';
  } else {
    return 'guest_registered';
  }
};

type ModalTypes = {
  name: string;
  resetForm?: () => void;
  onClose: () => void;
  state?: StatusModalStates;
  errorMessage?: string;
  modalTitle?: string;
  isEdition?: boolean;
  navigateOnSuccess?: boolean;
};

function ModalGuestRegistered({
  name,
  state,
  onClose,
  errorMessage,
  modalTitle,
  isEdition,
  navigateOnSuccess = true,
}: ModalTypes) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {getFullPath} = usePath();
  const timeoutRef = useTimeoutRef();
  const title = getTitle(state, modalTitle);

  const finishProcess = React.useCallback(() => {
    const summary = queryClient.getQueryData<BookingSummary>([QUERY_CACHE_KEYS.summary]);

    const path =
      summary?.guest_registration?.status === 'COMPLETE'
        ? PATHS.home
        : PATHS.guestRegistration;

    if (navigateOnSuccess) {
      navigate(getFullPath(path), {replace: true});
    }

    onClose();
  }, [getFullPath, navigate, onClose, queryClient, navigateOnSuccess]);

  React.useEffect(() => {
    if (state === StatusModalStates.SUCCESS) {
      timeoutRef.current = setTimeout(() => {
        finishProcess();
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [finishProcess, state, timeoutRef]);

  return (
    <>
      <StyledModalGuestRegistered>
        <ModalHeader>
          {/* <Row>
            <img src={gif} alt="Guest registered!" width={110} height={110} />
          </Row> */}
          <Row className="modalTitle">
            <ModalTitleText>{t(title)}</ModalTitleText>
          </Row>
        </ModalHeader>
        <ModalBody>
          <Row>
            <ModalSubtitle>
              {state === StatusModalStates.LOADING && <>{t('it_could_take_seconds')}</>}
              {state === StatusModalStates.SUCCESS && (
                <>
                  {isEdition ? (
                    <Trans i18nKey="guest_updated_modal_description" values={{name}}>
                      <b>{name}</b> has been successfully updated.
                    </Trans>
                  ) : (
                    <Trans i18nKey="guest_registered_modal_description" values={{name}}>
                      <b>{name}</b> has been successfully registered.
                    </Trans>
                  )}
                </>
              )}
              {state === StatusModalStates.ERROR && (
                <>
                  <ErrorMessage>{errorMessage}</ErrorMessage>
                  <CloseButton
                    width={195}
                    primary
                    label={t('ok')}
                    onClick={() => onClose()}
                  />
                </>
              )}
            </ModalSubtitle>
          </Row>
        </ModalBody>
      </StyledModalGuestRegistered>
    </>
  );
}

export {ModalGuestRegistered};
